import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { FadeIn } from './ui/FadeIn';
import { CTAButton } from './ui/CTAButton';
import { FloatingElements } from './ui/FloatingElements';
import { ShieldCheckIcon, ZapIcon, HeartIcon } from 'lucide-react';
const ORIGINAL_PRICE = 19;
const CURRENT_PRICE = 6.36;
export function PricingSection() {
  const count = useMotionValue(ORIGINAL_PRICE);
  const [displayPrice, setDisplayPrice] = useState(ORIGINAL_PRICE);
  useEffect(() => {
    const unsubscribe = count.on('change', latest => setDisplayPrice(latest));
    return () => unsubscribe();
  }, []);
  return <section id="pricing" className="py-32 px-4 md:px-8 relative overflow-hidden" data-id="element-189">
      {/* Background Image */}
      <img src="https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=1400&q=80" alt="Ambiente cálido y acogedor con luz suave" className="absolute inset-0 w-full h-full object-cover z-0" data-id="element-190" />
      {/* Very Heavy Warm Overlay */}
      <div className="absolute inset-0 bg-cream-light/[0.92] z-[1]" data-id="element-191"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-cream/60 via-transparent to-cream/60 z-[2]" data-id="element-192"></div>

      <FloatingElements count={5} colors={['bg-gold', 'bg-rose']} data-id="element-193" />

      <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-194">
        <FadeIn data-id="element-195">
          <p className="text-2xl md:text-3xl text-charcoal leading-relaxed mb-16 font-serif italic max-w-2xl mx-auto" data-id="element-196">
            "Recuperar tu paz mental y el fuego de tu hogar no debería costar
            una fortuna en terapia."
          </p>
        </FadeIn>

        <FadeIn delay={0.2} className="relative" data-id="element-197">
          {/* Pulsing Border Glow */}
          <motion.div animate={{
          opacity: [0.4, 0.8, 0.4],
          scale: [0.98, 1.02, 0.98]
        }} transition={{
          repeat: Infinity,
          duration: 4,
          ease: 'easeInOut'
        }} className="absolute -inset-1 bg-gradient-to-r from-gold via-rose to-gold rounded-[3rem] blur-lg opacity-50" data-id="element-198" />

          <div className="bg-white/90 backdrop-blur-md p-12 md:p-16 rounded-[3rem] shadow-2xl border border-white relative z-10" data-id="element-199">
            {/* Ebook Cover */}
            <img src="https://cdn.magicpatterns.com/uploads/diq7zAUvEWWs5qmFtj8syT/heylloyd-image-1773698007028.png" alt="Portada del ebook: Los 7 Síntomas Silenciosos" className="w-40 md:w-48 mx-auto mb-8 rounded-xl shadow-lg border border-white/50" data-id="element-200" />

            {/* Discount Badge */}
            <motion.div initial={{
            scale: 0,
            rotate: -12
          }} whileInView={{
            scale: 1,
            rotate: -6
          }} viewport={{
            once: true
          }} transition={{
            type: 'spring',
            bounce: 0.5,
            delay: 0.4
          }} className="absolute -top-5 -right-3 md:right-8 bg-burgundy text-white font-bold text-lg px-4 py-2 rounded-2xl shadow-lg z-20 border-2 border-white" data-id="element-201">
              -66%
            </motion.div>

            <h3 className="text-2xl text-charcoal-light mb-8 font-medium" data-id="element-202">
              Consigue la guía completa por solo
            </h3>

            {/* Price with strikethrough */}
            <div className="flex items-center justify-center gap-4 md:gap-6 mb-4" data-id="element-203">
              <span className="text-3xl md:text-4xl font-serif text-charcoal-light/50 line-through decoration-burgundy/60 decoration-2" data-id="element-204">
                ${ORIGINAL_PRICE}
              </span>

              <motion.div onViewportEnter={() => animate(count, CURRENT_PRICE, {
              duration: 2.5,
              ease: 'easeOut'
            })} viewport={{
              once: true
            }} className="text-7xl md:text-8xl font-serif font-bold text-burgundy-dark flex items-baseline gap-1" data-id="element-205">
                <span data-id="element-206">$</span>
                <span data-id="element-207">{displayPrice.toFixed(2)}</span>
              </motion.div>

              <span className="text-2xl md:text-3xl text-charcoal-light font-sans font-normal self-end mb-3" data-id="element-208">
                USD
              </span>
            </div>

            <p className="text-charcoal-light mb-12 bg-cream inline-block px-8 py-3 rounded-full text-sm md:text-base font-medium border border-cream-dark" data-id="element-209">
              Un solo pago, acceso de por vida
            </p>

            <CTAButton className="mb-10" redirectToCart={true} data-id="element-210" />

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12 pt-8 border-t border-cream-dark" data-id="element-211">
              <div className="flex items-center gap-2 text-charcoal-light" data-id="element-212">
                <ShieldCheckIcon className="w-5 h-5 text-gold-dark" data-id="element-213" />
                <span className="text-sm font-medium" data-id="element-214">Pago Seguro</span>
              </div>
              <div className="flex items-center gap-2 text-charcoal-light" data-id="element-215">
                <ZapIcon className="w-5 h-5 text-gold-dark" data-id="element-216" />
                <span className="text-sm font-medium" data-id="element-217">Acceso Inmediato</span>
              </div>
              <div className="flex items-center gap-2 text-charcoal-light" data-id="element-218">
                <HeartIcon className="w-5 h-5 text-gold-dark" data-id="element-219" />
                <span className="text-sm font-medium" data-id="element-220">Hecho con Amor</span>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}